import React, { useMemo } from "react";
import {
    SovosBox,
    SovosButton,
    SovosSwitch,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { DataGrid } from "colbi_web_ui/lib/components";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { formatConditionSummary } from "./featureFlags";
import RulesToolbar from "./RulesToolbar";

export const formatRuleTimestamp = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const RulesCell = ({ type, value }) => {
    if (type === "ruleName") {
        const { onOpenRule, rule } = value;
        return (
            <SovosButton
                onClick={(event) => {
                    event.stopPropagation();
                    onOpenRule(rule);
                }}
                size="small"
                sx={{ justifyContent: "flex-start", minWidth: 0, p: 0 }}
                variant="text"
            >
                {rule.name}
            </SovosButton>
        );
    }

    if (type === "ruleStatus") {
        const {
            canMutate,
            nonOwnerTooltip,
            onToggleState,
            rule,
        } = value;
        const processing = rule.state === "Processing";
        const disabled = processing || !canMutate;
        const tooltip = !canMutate
            ? nonOwnerTooltip
            : processing
              ? "This rule is processing."
              : "";
        return (
            <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                <SovosTooltip placement="top" title={tooltip}>
                    <span>
                        <SovosSwitch
                            aria-label={`Toggle ${rule.name}`}
                            checked={rule.state !== "Inactive"}
                            disabled={disabled}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => onToggleState(rule)}
                        />
                    </span>
                </SovosTooltip>
                <SovosTypography
                    sx={{ color: processing ? "text.disabled" : "text.primary" }}
                    variant="body2"
                >
                    {rule.state}
                </SovosTypography>
            </SovosBox>
        );
    }

    return <SovosTypography variant="body2">{value ?? "—"}</SovosTypography>;
};

const RulesTable = ({
    canMutate,
    filter,
    loading,
    nonOwnerTooltip,
    onOpenRule,
    onStatusFilterChange,
    onToggleState,
    page,
    pageSize,
    rules,
    sort,
    statusFilter,
    total,
}) => {
    const { i18n } = useGlobalValue();
    const availableFilters = useMemo(
        () => [
            { name: "Rule Name", prop: "name", type: "string" },
            { name: "Description", prop: "description", type: "string" },
            { name: "Last Run Fixes", prop: "lastRunFixes", type: "int" },
            { name: "Last Run", prop: "lastRun", type: "date" },
            {
                list: ["Active", "Inactive", "Processing"],
                name: "Status",
                prop: "status",
                type: "autosearch",
            },
        ],
        [],
    );
    const header = useMemo(
        () => [
            {
                basis: "20%",
                columnName: "name",
                headerName: i18n`Rule Name`,
                label: ["Rule Name"],
                minWidth: 180,
                type: "ruleName",
            },
            {
                basis: "30%",
                columnName: "description",
                headerName: i18n`Description`,
                label: ["Description"],
                minWidth: 260,
                sortable: false,
            },
            {
                align: "right",
                basis: "15%",
                columnName: "lastRunFixes",
                headerName: i18n`Last Run Fixes`,
                label: ["Last Run Fixes"],
                minWidth: 130,
            },
            {
                basis: "20%",
                columnName: "lastRun",
                headerName: i18n`Last Run`,
                label: ["Last Run"],
                minWidth: 180,
            },
            {
                basis: "15%",
                columnName: "status",
                headerName: i18n`Status`,
                label: ["Status"],
                minWidth: 150,
                sortable: false,
                type: "ruleStatus",
            },
        ],
        [i18n],
    );
    const items = useMemo(
        () => rules.map((rule) => ({
            card: [
                [{ type: "ruleName", value: { onOpenRule, rule } }],
                [{ value: formatConditionSummary(rule, i18n) }],
                [{ value: rule.last_run_changed_rows ?? "—" }],
                [{ value: formatRuleTimestamp(rule.last_run_at) }],
                [{
                    type: "ruleStatus",
                    value: {
                        canMutate,
                        nonOwnerTooltip,
                        onToggleState,
                        rule,
                    },
                }],
            ],
            id: rule.id,
        })),
        [canMutate, i18n, nonOwnerTooltip, onOpenRule, onToggleState, rules],
    );

    return (
        <SovosBox sx={{ minHeight: 360 }}>
            <DataGrid
                key={`${filter}-${sort}-${page}-${pageSize}`}
                availableFilters={availableFilters}
                card={RulesCell}
                enablePagination
                filter={filter}
                header={header}
                headerHeight={48}
                id="rules-data-grid"
                items={items}
                metadata={{ total }}
                modifierssx={{
                    "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                }}
                status={{ loading }}
                page={page}
                pageSize={pageSize}
                sort={sort}
                toolbar={RulesToolbar}
                toolbarProps={{
                    onStatusFilterChange,
                    statusFilter,
                }}
            />
        </SovosBox>
    );
};

export default RulesTable;
